import {
  BoxGeometry,
  Color,
  EdgesGeometry,
  Euler,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  Object3DEventMap,
  Scene,
  Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
} from "three";
import { CSS3DObjectWallMenu } from "../../core/input/CSS3DWallMenu";
import { CameraInterface } from "../../engine/Engine";
import { Line } from "../Line";
import { Meter } from "../Meter";
import { Point } from "../Point";

export abstract class AbstractWall extends Line {
  protected _height: number = 2.2;
  protected _thickness: number = 0.1;
  protected _meter!: Meter;
  protected _edges?: LineSegments;
  protected _edgeColor2d?: Color;
  protected _edgeColor3d?: Color;
  protected _mesh3dMaterial?: MeshStandardMaterial = new MeshStandardMaterial();
  protected _mesh2D: Mesh = new Mesh(
    new BoxGeometry(0.01, 0.01, 0.01),
    new MeshStandardMaterial()
  );
  protected _menu2D?: CSS3DObjectWallMenu;

  constructor(start: Point, end: Point) {
    super(start, end);
    this._root = new Object3D();
    this._root.userData["parent"] = this;
    this._isVisible = true;
    this.raycastable = false;
    this.initializeMeter();
    this.initializeColors();
    this.addEventListener("focusMeter", this.onFocusMeter.bind(this));
  }

  protected initializeMeter() {
    this._meter = new Meter(this, undefined, undefined, {
      minScale: 0.75,
      maxScale: 0.75,
      offset: new Vector3(0.0, -0.2, -0.2),
      rotation: new Euler(Math.PI / 2, 0, 0),
    });

    this._menu2D = new CSS3DObjectWallMenu(this, {
      minScale: 0.75,
      maxScale: 0.75,
      offset: new Vector3(-0.5, 0.0, -0.3),
      rotation: new Euler(Math.PI / 2, 0, 0),
    });
  }

  protected initializeColors() {
    this._color2d = new Color(0xaaaaaa);
    this._color3d = new Color(0xcccccc);
  }

  protected onFocusMeter() {
    this._meter.focus(false);
  }

  get isConstruct(): boolean {
    return true;
  }

  get hidden(): boolean {
    return this._hidden;
  }

  set hidden(value: boolean) {
    if (this._root) this._root.visible = value && this._isVisible;
    this._hidden = value;
  }

  get visible(): boolean {
    return this._isVisible;
  }

  set visible(value: boolean) {
    if (this._root) this._root.visible = value && !this._hidden;
    this._isVisible = value;
  }

  get raycastingTarget(): Object3D<Object3DEventMap> {
    return this._root!;
  }

  get showEdges(): boolean {
    return this.edges && this.edges?.visible;
  }

  set showEdges(value: boolean) {
    if (this.edges) this.edges.visible = value;
  }

  get edges() {
    if (!this._edges) {
      const geo = new EdgesGeometry(this._mesh2D.geometry);
      const line = new LineSegments(
        geo,
        new MeshBasicMaterial({ color: 0x000000, depthTest: false })
      );
      line.renderOrder = 999;
      line.name = "lineSegments_of_" + this.name;
      line.geometry.boundingSphere = null;
      line.geometry.computeBoundingBox();
      this._edges = line;
      this._edges.raycast = () => {};
      this._edges.visible = false;

      this._mesh2D!.add(this._edges);
    } else if (this._edges && this._mesh2D.geometry) {
      this._edges.visible = false;
      this._edges.geometry.dispose();
      const geo = new EdgesGeometry(this._mesh2D.geometry);
      this._edges.geometry.boundingSphere = null;
      this._edges.geometry.computeBoundingBox();
      this._edges.geometry.copy(geo);

      this._mesh2D!.add(this._edges);
    }
    return this._edges;
  }

  get collider(): Object3D<Object3DEventMap> | undefined {
    this._mesh2D.userData["owner"] = this;
    return this._mesh2D;
  }

  protected _scaleObb: Vector3 = new Vector3();
  get scaleObb(): Vector3 {
    this._scaleObb.set(this.length, this._height, this._thickness);
    return this._scaleObb;
  }

  private static readonly _obbWorldUp = new Vector3(0, 1, 0);

  /**
   * Merkez: çizgi ortası + dünya Y’de yarım yükseklik (zeminden tavana).
   * Kalınlık: mesh iç yüzeyden dışa doğru uzanır; OBB’yi çizgi üzerinde simetrik
   * bırakmak oda tarafına yarım kalınlık kaydırırdı — −s.z/2 ile hizalanır.
   */
  protected override updateObb() {
    const d = this.direction;
    const u = AbstractWall._obbWorldUp;
    const n = this.normal;
    const s = this.scaleObb;
    const obb = this._worldOBB;
    if (!obb) return;
    obb.center.copy(this.point).addScaledVector(u, s.y * 0.5);
    if (n) {
      obb.center.addScaledVector(n, -s.z * 0.5);
    }
    obb.halfSize.set(s.x * 0.5, s.y * 0.5, s.z * 0.5);
    if (d && n) {
      obb.rotation.set(d.x, d.y, d.z, u.x, u.y, u.z, n.x, n.y, n.z);
    }
  }

  removeFromParent(): void {
    super.removeFromParent();
    this._root?.removeFromParent();
    this._meter?.root.removeFromParent();
    this._menu2D?.removeFromParent();
  }

  protected createBaseShape() {
    const shape = new Shape([
      new Vector2(-this.length * 0.5, 0.0),
      new Vector2(-this.length * 0.5, this._thickness),
      new Vector2(this.length * 0.5, this._thickness),
      new Vector2(this.length * 0.5, 0.0),
    ]);
    return shape;
  }

  private _createBase2DGeometry() {
    const shape = this.createBaseShape();
    return new ShapeGeometry(shape);
  }

  private _render2D(color: Color) {
    const mesh = this._mesh2D;
    mesh.name = this.name;
    mesh.geometry?.dispose();
    mesh.geometry = this._createBase2DGeometry();
    mesh.rotation.set(-Math.PI * 0.5, 0, 0);

    this._root?.add(mesh);
    this._root?.position.copy(this._point!);
    this._root?.quaternion.copy(this.rotation!);

    (mesh.material as MeshStandardMaterial).color = color;

    mesh.updateMatrixWorld(true);
  }

  protected renderMeter(scene?: Scene, camera?: CameraInterface) {
    if (!this._meter) {
      this._meter = new Meter(this, undefined, undefined, {
        minScale: 0.75,
        maxScale: 0.75,
        offset: new Vector3(0.0, -0.2, -0.2),
        rotation: new Euler(Math.PI / 2, 0, 0),
      });
    }
    this._meter.isDirty = true;
    this._meter.parameters.rotation?.set(Math.PI / 2, Math.PI, 0);
    this._root!.add(this._meter.root);
    this._meter.render(scene, camera);
  }

  protected renderMenu(scene?: Scene, camera?: CameraInterface) {
    if (this._menu2D) {
      this._menu2D.update();
      this._root!.add(this._menu2D);
    }
  }

  public render2d(scene?: Scene, camera?: CameraInterface) {
    scene?.add(this._root!);
    this.root?.clear();
    this._render2D(this._color2d!);
    this._root!.visible = this.visible && !this.hidden;

    this.renderMeter(scene, camera);
    this.renderMenu(scene, camera);
    this.showEdges = true;

    this._childs?.forEach((c) => {
      c.update();
      c.root?.removeFromParent();
      this._root?.add(c.root!);
    });
  }

  public render3d(scene?: Scene, camera?: CameraInterface) {
    this.render2d(scene, camera);
  }

  render(scene?: Scene, camera?: CameraInterface): void {
    this._mesh2D.removeFromParent();
    this._menu2D?.removeFromParent();
    this._meter?.removeFromParent();
    this.root?.clear();
    super.render(scene, camera);
  }

  dispose(scene?: Scene): void {
    super.dispose(scene);
    this._root?.removeFromParent();
    this._root?.traverse((m) => {
      if (m instanceof Mesh) m.geometry.dispose();
    });
    this._meter?.dispose(scene);
    this._menu2D?.dispose(scene);
  }
}
